import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import styles from "./ServiceDetail.module.css";
import SEO from "../components/SEO";
import ServiceContactForm from "../components/ServiceContactForm";
import skilledNursing from "../assets/images/skilled-nursing.png";
import physicalTherapy from "../assets/images/physical-therapy.png";
import ocupationTherapy from "../assets/images/ocupation-therapy.png";
import speechTherapy from "../assets/images/speech-therapy.png";
import socialService from "../assets/images/social-service.png";
import demantiaHelp from "../assets/images/demantia-help.png";
import geriatricCare from "../assets/images/geriatric-care.png";
import medicationReminders from "../assets/images/medication-reminders.png";
import postHospitalizationCare from "../assets/images/post-hospitalization-care.png";
import about1 from "../assets/images/about-1.png";
import about2 from "../assets/images/about-2.png";
import {
    FaUserNurse,
    FaWalking,
    FaHandHoldingHeart,
    FaComments,
    FaUsers,
    FaHome,
    FaBrain,
    FaClipboardList,
    FaPills,
    FaHospital,
    FaArrowRight,
    FaChevronLeft,
    FaChevronRight,
} from "react-icons/fa";

const PhysicalTherapy = () => {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 767);
    const [isTablet, setIsTablet] = useState(window.innerWidth > 767 && window.innerWidth <= 991);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 767);
            setIsTablet(window.innerWidth > 767 && window.innerWidth <= 991);
        };

        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    // All services
    const allServices = [
        {
            id: 1,
            title: "Skilled Nursing",
            description:
                "Registered nurses provide skilled care and instruction to patients and families, including wound care, medication supervision, catheter care, treatments and injections, and I.V. therapy.",
            image: skilledNursing,
            icon: <FaUserNurse />,
            link: "/services/skilled-nursing",
        },
        {
            id: 2,
            title: "Physical Therapy",
            description:
                "Licensed physical therapists help patients regain strength, mobility, and independence through personalized treatment plans.",
            image: physicalTherapy,
            icon: <FaWalking />,
            link: "/services/physical-therapy",
        },
        {
            id: 3,
            title: "Occupational Therapy",
            description:
                "Occupational therapists assist patients in developing, recovering, and maintaining daily living skills for improved quality of life.",
            image: ocupationTherapy,
            icon: <FaHandHoldingHeart />,
            link: "/services/occupational-therapy",
        },
        {
            id: 4,
            title: "Speech Therapy",
            description: "Speech-language pathologists help patients improve communication, swallowing difficulties, and cognitive skills.",
            image: speechTherapy,
            icon: <FaComments />,
            link: "/services/speech-therapy",
        },
        {
            id: 5,
            title: "Social Services",
            description:
                "Social workers provide counseling, community resource connections, and support for patients and families navigating healthcare challenges.",
            image: socialService,
            icon: <FaUsers />,
            link: "/services/social-services",
        },
        {
            id: 6,
            title: "Certified Home Health Aid",
            description:
                "Certified aides assist patients with personal care, meal preparation, ambulation, and exercises under professional supervision to support independent living at home.",
            image: about1,
            icon: <FaHome />,
            link: "/services/certified-home-health-aid",
        },
        {
            id: 7,
            title: "Alzheimer's Care",
            description:
                "Specialized care and support for patients with Alzheimer's disease and dementia, focusing on safety, dignity, and quality of life.",
            image: demantiaHelp,
            icon: <FaBrain />,
            link: "/services/alzheimers-care",
        },
        {
            id: 8,
            title: "Geriatric Care Management",
            description:
                "Comprehensive care coordination and advocacy services to help seniors and their families navigate complex healthcare and social service systems.",
            image: geriatricCare,
            icon: <FaClipboardList />,
            link: "/services/geriatric-care-management",
        },
        {
            id: 9,
            title: "Medication Reminders",
            description:
                "Professional medication management services to ensure proper medication adherence and prevent missed doses or dangerous interactions.",
            image: medicationReminders,
            icon: <FaPills />,
            link: "/services/medication-reminders",
        },
        {
            id: 10,
            title: "Post-hospitalization Care",
            description:
                "Transitional care services to support safe recovery at home after hospital discharge, reducing readmission risks and promoting healing.",
            image: postHospitalizationCare,
            icon: <FaHospital />,
            link: "/services/post-hospitalization-care",
        },
    ];

    // Filter out current service (Physical Therapy)
    const relatedServices = allServices.filter((service) => service.id !== 2);

    const getMaxIndex = () => {
        if (isMobile) return relatedServices.length - 1;
        if (isTablet) return relatedServices.length - 2;
        return relatedServices.length - 3;
    };

    // Auto-scroll functionality
    useEffect(() => {
        const maxIndex = getMaxIndex();

        const interval = setInterval(() => {
            setCurrentIndex((prev) => {
                if (prev >= maxIndex) return 0;
                return prev + 1;
            });
        }, 5000);

        return () => clearInterval(interval);
    }, [isMobile, isTablet, relatedServices.length]);

    const nextSlide = () => {
        const maxIndex = getMaxIndex();
        setCurrentIndex((prev) => {
            if (prev >= maxIndex) return 0;
            return prev + 1;
        });
    };

    const prevSlide = () => {
        const maxIndex = getMaxIndex();
        setCurrentIndex((prev) => {
            if (prev <= 0) return maxIndex;
            return prev - 1;
        });
    };

    return (
        <div className={styles.serviceDetailPage}>
            <SEO
                title="Physical Therapy Services"
                description="Professional physical therapy at home in Southern California. Improve mobility, strength, balance, and reduce pain with our licensed physical therapists."
                keywords="physical therapy, PT, mobility improvement, strength training, balance therapy, pain management, rehabilitation"
                canonicalUrl="https://allforyouhc.com/services/physical-therapy"
            />
            {/* Hero Section */}
            <section className={styles.heroSection}>
                <div className={styles.heroContainer}>
                    <div className={styles.heroContent}>
                        <div className={styles.iconBadge}>
                            <FaWalking />
                        </div>
                        <p className={styles.heroTag}>SENIOR CARE SERVICES</p>
                        <h1 className={styles.heroTitle}>Physical Therapy</h1>
                        <p className={styles.heroDescription}>
                            Expert physical therapy services designed to help you regain strength, improve mobility, and achieve independence through
                            personalized treatment plans delivered in the comfort of your home.
                        </p>
                        <Link to="/contact" className={styles.heroButton}>
                            Get Started
                        </Link>
                    </div>
                    <div className={styles.heroImageContainer}>
                        <img src={physicalTherapy} alt="Physical Therapy" className={styles.heroImage} />
                    </div>
                </div>
            </section>

            {/* What's Included Section */}
            <section className={styles.includedSection}>
                <div className={styles.container}>
                    <div className={styles.includedGrid}>
                        <div className={styles.includedContent}>
                            <h2 className={styles.sectionTitle}>What's included in the service?</h2>
                            <p className={styles.includedText}>
                                Our physical therapy program includes comprehensive evaluation and treatment services tailored to your individual
                                needs. Each session is designed to maximize your recovery and help you achieve your mobility goals.
                            </p>
                            <ul className={styles.includedList}>
                                <li>Comprehensive initial evaluation and ongoing assessments</li>
                                <li>Personalized treatment plans based on your specific needs and goals</li>
                                <li>Therapeutic exercises to improve strength and flexibility</li>
                                <li>Gait training and balance exercises to prevent falls</li>
                                <li>Manual therapy techniques for pain relief and improved mobility</li>
                                <li>Post-surgical rehabilitation and recovery programs</li>
                                <li>Orthopedic and neurological rehabilitation</li>
                                <li>Patient and caregiver education on home exercise programs</li>
                                <li>Assistive device training (walkers, canes, etc.)</li>
                                <li>Progress monitoring and coordination with your physician</li>
                            </ul>
                        </div>
                        <div className={styles.includedImage}>
                            <img src={about2} alt="Physical Therapy Session" />
                        </div>
                    </div>
                </div>
            </section>

            {/* About Service Section */}
            <section className={styles.aboutSection}>
                <div className={styles.container}>
                    <div className={styles.aboutGrid}>
                        <div className={styles.aboutContent}>
                            <h2 className={styles.sectionTitle}>About this service</h2>
                            <p className={styles.aboutText}>
                                Our Physical Therapy services bring expert rehabilitation care directly to your home. Our licensed physical therapists
                                specialize in helping patients recover from injuries, surgeries, and managing chronic conditions that affect mobility
                                and physical function.
                            </p>
                            <p className={styles.aboutText}>
                                Whether you're recovering from a fall, joint replacement, stroke, or dealing with chronic pain, our therapists create
                                customized treatment plans focused on your specific goals. We use evidence-based techniques and therapeutic exercises
                                to help you regain strength, improve balance, reduce pain, and return to the activities you love.
                            </p>
                        </div>
                        <div className={styles.contactCard}>
                            <h3 className={styles.contactTitle}>Get in contact with us!</h3>
                            <p className={styles.contactText}>
                                Ready to start your journey to better mobility? Contact us today to schedule an evaluation with one of our physical
                                therapists.
                            </p>
                            <ServiceContactForm
                                serviceName="Physical Therapy"
                                serviceType="Physical Therapy Consultation"
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* More Services Section */}
            <section className={styles.moreServicesSection}>
                <div className={styles.container}>
                    <div className={styles.moreServicesHeader}>
                        <h2 className={styles.sectionTitle}>More services</h2>
                        <Link to="/services" className={styles.browseButton}>
                            Browse services
                        </Link>
                    </div>
                    <div className={styles.sliderWrapper}>
                        <button className={styles.navButton + " " + styles.navButtonPrev} onClick={prevSlide} aria-label="Previous services">
                            <FaChevronLeft />
                        </button>

                        <div className={styles.sliderContainer}>
                            <div
                                className={styles.servicesSlider}
                                style={{
                                    transform: `translateX(-${currentIndex * (isMobile ? 100 : isTablet ? 50 : 33.333)}%)`,
                                    transition: "transform 0.5s ease-in-out",
                                }}
                            >
                                {relatedServices.map((service) => (
                                    <div key={service.id} className={styles.slideItem}>
                                        <Link to={service.link} className={styles.serviceCard}>
                                            <div className={styles.serviceImageContainer}>
                                                <img src={service.image} alt={service.title} className={styles.serviceImage} />
                                                <div className={styles.serviceIconBadge}>{service.icon}</div>
                                            </div>
                                            <div className={styles.serviceContent}>
                                                <h3 className={styles.serviceTitle}>{service.title}</h3>
                                                <p className={styles.serviceDescription}>{service.description}</p>
                                                <div className={styles.serviceLink}>
                                                    <span>View service</span>
                                                    <FaArrowRight />
                                                </div>
                                            </div>
                                        </Link>
                                    </div>
                                ))}
                            </div>
                        </div>

                        <button className={styles.navButton + " " + styles.navButtonNext} onClick={nextSlide} aria-label="Next services">
                            <FaChevronRight />
                        </button>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default PhysicalTherapy;
